import { useState, useRef, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import MoreDropdown from '@/components/navbar/MoreDropdown'
import { fitNavItems, NAV_LINKS, TOGGLE_ITEMS } from '@/components/navbar/navItems'
import { CollapseVariantsToggle, ShowHiddenToggle } from '@/components/navbar/PriorityNavToggles'
import { useFeatureFlag } from '@/hooks/ui/useFeatureFlags'
import { ChevronDownIcon } from '@/icons'
import { useAuthStore } from '@/store/authStore'

const linkStyles = 'px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap'
const activeLinkStyles = 'bg-elevated text-primary'
const inactiveLinkStyles = 'text-secondary hover:bg-hover hover:text-primary'
const moreButtonStyles =
  'flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-secondary hover:bg-hover hover:text-primary whitespace-nowrap'

function isActivePath(pathname: string, path: string) {
  if (path === '/browse/cards') return pathname.startsWith('/browse')
  if (path === '/list/buy') return pathname.startsWith('/list')
  return pathname === path
}

/** Tracks the container's content width via ResizeObserver. */
function useContainerWidth(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [availableWidth, setAvailableWidth] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setAvailableWidth(entry.contentRect.width)
      }
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef])

  return availableWidth
}

export default function PriorityNav() {
  const [moreOpen, setMoreOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const availableWidth = useContainerWidth(containerRef)
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()
  const collectionEnabled = useFeatureFlag('collection_screen')

  // Close dropdown on route change
  useEffect(() => {
    setMoreOpen(false)
  }, [location.pathname])

  // Build lists based on auth status and feature flags
  const links = useMemo(
    () =>
      NAV_LINKS.filter((item) => {
        if (item.requiresAuth && !isAuthenticated) return false
        if (item.id === 'collection' && !collectionEnabled) return false
        return true
      }),
    [isAuthenticated, collectionEnabled],
  )
  const toggles = isAuthenticated ? TOGGLE_ITEMS : []

  const { visibleLinks, visibleToggles, overflowItems } = fitNavItems(links, toggles, availableWidth)

  return (
    <div ref={containerRef} className="hidden md:flex items-center gap-1 flex-1 min-w-0 relative">
      {/* Nav links on the left */}
      {visibleLinks.map((item) => {
        const active = isActivePath(location.pathname, item.to as string)
        return (
          <Link
            key={item.id}
            to={item.to as string}
            aria-current={active ? 'page' : undefined}
            className={`${linkStyles} ${active ? activeLinkStyles : inactiveLinkStyles}`}
          >
            {item.label}
          </Link>
        )
      })}

      {/* Spacer pushes toggles to the right */}
      <div className="flex-1" />

      {/* Toggles on the right */}
      {visibleToggles.map((item) => {
        if (item.id === 'collapse-toggle') return <CollapseVariantsToggle key={item.id} />
        if (item.id === 'hidden-toggle') return <ShowHiddenToggle key={item.id} />
        return null
      })}

      {/* More dropdown for overflow items */}
      {overflowItems.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            className={moreButtonStyles}
          >
            More
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
          </button>
          <MoreDropdown isOpen={moreOpen} onClose={() => setMoreOpen(false)} items={overflowItems} />
        </div>
      )}
    </div>
  )
}
